/**
 * Redirect URI rules, mirrored from `internal/admin/redirect_uri.go`.
 *
 * THE SERVER IS THE CONTROL. `ValidateRedirectURIs` is what actually decides
 * whether a callback may be registered, and 0029's unique index on
 * `auth.oauth_client_redirect_uris.normalized_uri` is what actually decides
 * whether two clients may share one. Everything here is a courtesy that lets
 * an operator hear the rule while they are typing instead of after they have
 * confirmed a reason-carrying write — and the console shows the API's own
 * sentence verbatim whenever the two ever disagree.
 *
 * Kept in a `.ts` module with no JSX and no imports on purpose: that is the
 * only shape this workspace can execute directly under Node's type stripping,
 * and it is the only unit-test this portal has (there is no test runner).
 *
 * The two rules here are NOT the same rule, exactly as the Go file's header
 * argues:
 *
 *   * `redirectURIError` asks "may this be registered at all" — a property of
 *     one entry, answered at the form, a 400 on the wire.
 *   * `normalizeRedirectURI` decides what makes two spellings the SAME
 *     callback. Within one list that is a form error; across clients it is
 *     0029's index and a 409, which this file cannot see and does not guess at.
 */

/** 0029's projection trigger unnests this list on every write, so the cost of
 *  a write is linear in it and the caller must not choose how linear. Both
 *  bounds are `internal/admin/redirect_uri.go`'s. */
export const MAX_REDIRECT_URIS = 16;
const MAX_REDIRECT_URI_LENGTH = 2048;

/**
 * `auth.normalize_redirect_uri`'s regex, character for character — the same
 * one Go copied rather than adapted. scheme, host, optional port, and
 * everything from the first `/`, `?` or `#` onwards.
 */
const URI_PATTERN = /^([A-Za-z][A-Za-z0-9+.-]*):\/\/([^/?#]*?)(:[0-9]+)?([/?#].*)?$/;

/** The hosts RFC 8252 §7.3 lets a native app bind an ephemeral listener on.
 *  The whole of 127.0.0.0/8, matching `loopbackHostPattern`. */
const LOOPBACK_PATTERN =
  /^http:\/\/(127\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}|\[::1\]|localhost)(:[0-9]+)?([/?#]|$)/;

/**
 * Spaces only — NOT `String.prototype.trim`.
 *
 * This looks like a bug and is not. The database trims with `btrim/1`, which
 * removes SPACES and not tabs or newlines, and Go mirrors that with
 * `strings.Trim(s, " ")` for the same reason. Using `trim()` here would make a
 * tab-padded URI normalise to something the index has never seen. A URI
 * carrying a tab is refused one layer up, where it can be explained.
 */
function trimSpaces(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === " ") {
    start += 1;
  }
  while (end > start && value[end - 1] === " ") {
    end -= 1;
  }
  return value.slice(start, end);
}

/**
 * The comparison form: scheme and host lowercased, a default port for the
 * scheme stripped, path/query/fragment preserved byte for byte.
 *
 * A URI the pattern cannot parse falls through to its trimmed self, exactly as
 * the SQL and the Go do.
 */
export function normalizeRedirectURI(uri: string): string {
  const trimmed = trimSpaces(uri);
  const m = URI_PATTERN.exec(trimmed);
  if (m === null) {
    return trimmed;
  }
  const scheme = (m[1] ?? "").toLowerCase();
  const host = (m[2] ?? "").toLowerCase();
  let port = m[3] ?? "";
  const rest = m[4] ?? "";
  if ((scheme === "http" && port === ":80") || (scheme === "https" && port === ":443")) {
    port = "";
  }
  return `${scheme}://${host}${port}${rest}`;
}

/**
 * Whether a URI is an RFC 8252 §7.3 loopback callback — the class 0029 exempts
 * from cross-client uniqueness, because the port is ephemeral and two unrelated
 * desktop apps both registering `http://127.0.0.1:8721/callback` is the
 * documented case rather than a mistake. Tests the NORMALISED form.
 *
 * Exported although only `redirectURIError` calls it today: it is the one fact
 * about a callback that a screen can usefully SAY — "this one is exempt from
 * the cross-client rule" is why an operator can register a port another product
 * already has — and it is asserted against the Go and the SQL alongside
 * `normalizeRedirectURI`. Delete it if no screen has taken it up.
 */
export function redirectURIIsLoopback(uri: string): boolean {
  return LOOPBACK_PATTERN.test(normalizeRedirectURI(uri));
}

/**
 * Why one entry may not be registered, or undefined if it may.
 *
 * The checks are in `validateRedirectURI`'s order, and each sentence says the
 * same thing the server's does — an operator who fixes the row on the strength
 * of this message must not then be refused for the same reason in different
 * words.
 */
export function redirectURIError(raw: string): string | undefined {
  const uri = trimSpaces(raw);
  if (uri === "") {
    return "Required — type the callback, or remove the row.";
  }
  if (uri.length > MAX_REDIRECT_URI_LENGTH) {
    return `Longer than ${MAX_REDIRECT_URI_LENGTH} characters.`;
  }
  // Before the pattern, because a URI carrying a tab or a newline WOULD parse
  // and would then normalise differently here and in the database.
  if (/[ \t\r\n]/.test(uri)) {
    return "It contains whitespace. No callback does.";
  }
  if (uri.includes("*")) {
    return (
      "Wildcards are not supported — a callback is compared exactly, so a pattern would " +
      "either never match or match too much."
    );
  }
  if (uri.includes("#")) {
    return "It carries a fragment, which RFC 6749 §3.1.2 does not permit on a redirect uri.";
  }
  const m = URI_PATTERN.exec(uri);
  if (m === null) {
    return (
      "It is not an absolute uri — a callback needs a scheme and a host, as in " +
      "https://app.example.com/callback"
    );
  }
  if ((m[2] ?? "") === "") {
    return "It names no host.";
  }
  const scheme = (m[1] ?? "").toLowerCase();
  if (scheme === "https") {
    return undefined;
  }
  if (scheme === "http") {
    return redirectURIIsLoopback(uri)
      ? undefined
      : "Plain http is only allowed for a loopback callback (http://127.0.0.1:*, " +
          "http://[::1]:* or http://localhost:*): to any other host it delivers an " +
          "authorization code over the network in the clear.";
  }
  return `Scheme ${scheme} is not allowed. A callback is https, or http on a loopback host.`;
}

/** What is wrong with a list: one sentence per row, plus anything that is a
 *  property of the list rather than of any one row. */
export interface RedirectURIIssues {
  /** Indexed alongside the list the caller passed in. */
  rows: Array<string | undefined>;
  /** Empty, or too long — neither belongs against a row. */
  list?: string;
}

/**
 * Every refusal a list would earn, found before the write.
 *
 * Unlike the server, which returns the FIRST problem, this reports one per row:
 * a form that fixed sixteen callbacks one round trip at a time would be a form
 * an operator gives up on. The duplicate check compares the NORMALISED form, so
 * two spellings of one endpoint are flagged here rather than collapsing
 * silently — and the message quotes the earlier spelling, because
 * `https://App.example.com/cb` and `https://app.example.com:443/cb` are one
 * endpoint and an operator has to see both to understand the refusal.
 */
export function redirectURIIssues(uris: string[]): RedirectURIIssues {
  const rows: Array<string | undefined> = uris.map((uri) => redirectURIError(uri));

  const seen = new Map<string, string>();
  uris.forEach((raw, index) => {
    if (rows[index] !== undefined) {
      return;
    }
    const uri = trimSpaces(raw);
    const normalized = normalizeRedirectURI(uri);
    const first = seen.get(normalized);
    if (first === undefined) {
      seen.set(normalized, uri);
      return;
    }
    rows[index] =
      first === uri
        ? "Listed twice."
        : `The same callback as ${first}, once the scheme and host are lowercased and a ` +
          "default port is stripped.";
  });

  if (uris.length === 0) {
    return {
      rows,
      list:
        "An OAuth client needs at least one redirect uri. A public client with no callback " +
        "cannot complete an authorization code flow, so emptying this list is not " +
        "“disable this client”, it is “break it” — use the status button for that.",
    };
  }
  if (uris.length > MAX_REDIRECT_URIS) {
    return { rows, list: `A client may register at most ${MAX_REDIRECT_URIS} redirect uris.` };
  }
  return { rows };
}

/** The list as it should travel: each entry trimmed the way the server trims
 *  it. Storing an untrimmed value would put a string in `redirect_uris` that
 *  `oauth_store.go`'s exact `==` can never match, so the client would be
 *  registered and unable to sign in. */
export function redirectURIsForSubmit(uris: string[]): string[] {
  return uris.map((uri) => trimSpaces(uri));
}
